import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';

// Import utils
import { CurrencyUtils } from '../../utils/currency';

@Component({
    selector: 'currency-input',
    templateUrl: './currency-input.component.html',
    styleUrls: ['./currency-input.component.scss'],
    standalone: false
})
export class CurrencyInputComponent {
    @Input() public value: number = null;
    @Input() public readonly: boolean = false;

    @Output() public valueChange = new EventEmitter<number>();

    @Output('focus') public onFocus = new EventEmitter<FocusEvent>();
    @Output('blur') public onBlur = new EventEmitter<void>();
    @Output('keydown') public onKeydown = new EventEmitter<KeyboardEvent>();

    @ViewChild('input') public _inputElement: ElementRef;

    constructor() { }

    /**
     * Focuses the input's content
     * @returns void
     */
    public focus(): void {
        this._inputElement.nativeElement.focus();
    }

    /**
     * Blurs the input's content (if it has focus)
     * @returns void
     */
    public blur(): void {
        this._inputElement.nativeElement.blur();
    }

    /**
     * Selects the input's content
     * @returns void
     */
    public select(): void {
        this._inputElement.nativeElement.select();
    }

    /* --- Event handlers --- */

    public _handleClick(event: MouseEvent) {
        // Prevent default
        event.preventDefault();

        // Perform focus
        this.focus();

        // Trigger event
        this.onFocus.emit(event);
    }

    public _handleFocus(event: FocusEvent) {
        // Stop propagation
        event.preventDefault();
        event.stopPropagation();
    }

    public _handleBlur() {
        this.onBlur.emit();
    }

    public _handleKeydown(event: KeyboardEvent) {
        this.onKeydown.emit(event);
    }

    public async _handleModelChange(value: string) {
        // Unformat the currency
        const unformattedValue = CurrencyUtils.unformatCurrency(value);

        // If the value is the same, clear the current value
        if (unformattedValue === this.value)
            await this.clearValue();

        // Assign the value
        this.value = unformattedValue ?? undefined;

        // Emit change event
        this.valueChange.emit(this.value);
    }

    public _purgeEvent(event: Event) {
        event.stopPropagation();
    }

    private async clearValue() {
        this.value = this.value === null ? 0 : null;
        return new Promise(resolve => {
            setTimeout(resolve, 0);
        });
    }
}
